import { Button } from '@cluey/cluey-components';
import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import { bindActionCreators } from 'redux';

import { submitCaseToSF } from '../../../actions/hub/hubContactPageActions';
import ErrorBlock from '../../../common/ErrorBlock';
import { ContainerLayout } from '../../../common/hub/ContainerLayout';
import withSelectedEnrolment from '../../../common/hub/NewWithSelectedEnrolment';
import { RadioGroup, useRadioGroupProps } from '../../../components/RadioGroup/RadioGroup';
import { ShadowContainer } from '../../../components/ShadowContainer';
import { TextArea, useTextAreaProps } from '../../../components/TextArea/TextArea';
import FormSubHeading from '../../../components/hub/contact/FormSubHeading';
import { PATH_HUB_CR_FEEDBACK, PATH_HUB_CR_SUBMIT_CASE_SUCCESS } from '../../../util/pagePath';
import { usePageHeader } from '../../../v2/app-shell/usePageHeader';
import { StudentEnrolmentDetails } from './StudentEnrolmentDetails';

const groupName = 'contact-form-feedback';
const radios = [
  {
    text: 'Session',
    value: 'Session',
    key: `${groupName}-category-1`,
  },
  {
    text: 'Tutor',
    value: 'Tutor',
    key: `${groupName}-category-2`,
  },
  {
    text: 'Service',
    value: 'Service',
    key: `${groupName}-category-3`,
  },
  {
    text: 'Other',
    value: 'Other',
    key: `${groupName}-category-4`,
  },
];

const FormFeedback = ({ selectedEnrolment, isSubmitting, submitError, errorMsg, submit }) => {
  const { setHeader } = usePageHeader();
  const location = useLocation();
  const headerLabel = location.state?.headerLabel ?? 'Report an issue';

  useEffect(() => {
    setHeader({
      label: headerLabel,
      showIcon: false,
      showBackButton: true,
    });
  }, [setHeader, headerLabel]);

  const radioGroupProps = useRadioGroupProps({ groupName, radios });
  const textAreaProps = useTextAreaProps({
    id: `${groupName}-textarea`,
    labelText: 'What would you like to tell us?',
  });

  const { enrolmentId, studentSfid, student, subject } = selectedEnrolment;
  const history = useHistory();
  const onClickHandler = () => {
    const caseType = 'Feedback';
    const caseCategory = radioGroupProps.selectedRadio;

    submit({
      uniqueEnrolmentId: enrolmentId,
      message: `Customer has provided feedback on ${caseCategory}. Details: ${textAreaProps.value}`,
      caseSubject: `Contact Request - ${caseType} - ${caseCategory}`,
      caseType,
      caseCategory,
      onSubmitSuccess: () => {
        history.push(PATH_HUB_CR_SUBMIT_CASE_SUCCESS, {
          firstName: student,
          studentId: studentSfid,
          header: 'Feedback submitted',
          message: `Your feedback on ${student}'s ${subject} enrolment has been submitted successfully. We'll be in touch soon.`,
        });
      },
    });
  };

  return (
    <ContainerLayout>
      <ShadowContainer className="flex flex-col gap-y-10 !rounded-3xl bg-white p-6 md:!p-20">
        <div className="flex flex-col gap-y-5">
          <FormSubHeading />
          {submitError && errorMsg && <ErrorBlock errorMsg={errorMsg} />}
          <StudentEnrolmentDetails student={student} subject={subject} selectedEnrolment={selectedEnrolment} />
        </div>
        <form>
          <RadioGroup {...radioGroupProps} className="mb-9" />
          <TextArea {...textAreaProps} className="mb-7" />
          <div className="mb-4 text-center tracking-normal lg:mb-8">
            <Button
              size="lg"
              disabled={!radioGroupProps.selectedRadio || !textAreaProps.value || isSubmitting}
              onClick={onClickHandler}
            >
              Submit feedback
            </Button>
          </div>
        </form>
      </ShadowContainer>
    </ContainerLayout>
  );
};

FormFeedback.propTypes = {
  selectedEnrolment: PropTypes.shape({
    enrolmentId: PropTypes.string.isRequired,
    studentSfid: PropTypes.string.isRequired,
    student: PropTypes.string.isRequired,
    subject: PropTypes.string.isRequired,
  }).isRequired,
  isSubmitting: PropTypes.bool.isRequired,
  submitError: PropTypes.bool.isRequired,
  errorMsg: PropTypes.string.isRequired,
  submit: PropTypes.func.isRequired,
};

export default connect(
  (state) => {
    return {
      isSubmitting: state.ui.apiState.hubContactPage.isLoading,
      submitError: state.ui.apiState.hubContactPage.fetchError,
      errorMsg: state.ui.apiState.hubContactPage.errorMsg,
    };
  },
  (dispatch) => {
    return {
      submit: bindActionCreators(submitCaseToSF, dispatch),
    };
  }
)(withSelectedEnrolment(FormFeedback, null, PATH_HUB_CR_FEEDBACK));
